import type { PageName } from '@ms/yammer-telemetry-support';
import type { Context } from 'react';
import { createContext } from 'react';

import type { HeroComponentName } from '../AppPageRoutesWithTelemetry/AppPageRoutesWithTelemetry.types';

export interface ReportHeroComponentRenderedErrorOptions {
  /**
   * Specify this as true if the component rendered with an error. This will end the page load measurement and correctly
   * report it as a failed load, instead of waiting until it eventually times out. Note that a timeout still counts as a
   * failed load, but letting your page timeout when there was an error makes your page's reliability data less accurate.
   */
  readonly endedWithError?: boolean;
}
export interface ReportHeroComponentRenderedRecoverableErrorOptions extends ReportHeroComponentRenderedErrorOptions {
  readonly endedWithError: true;

  /**
   * Specify this as true if the component provides the user with a recovery path, like a reload button. Do not set this
   * to true if the component only displays an error message without providing the user with a way to recover. This is
   * important to ensure accurate telemetry for your page in our shared perf + reliability dashboards.
   *
   * This is also critical for our Teams integrations, specifying the error as recoverable will:
   * 1. Instruct the host Teams app to immediately unhide our app's iframe
   * 2. Prevent Teams from eventually timing out our app's iframe and showing a full-screen error message
   * 3. Allow our page to handle the recovery in a more graceful way than having Teams reload our entire app
   * 4. Prevents these page load errors from counting against our app's reliability in Teams telemetry
   */
  readonly isRecoverable: boolean;
  readonly error?: Error;
}
export interface ReportHeroComponentRenderedExpectedTerminalErrorOptions extends ReportHeroComponentRenderedErrorOptions {
  readonly endedWithError: true;

  /**
   * Specify this as true if the component renders an *expected, terminal* state that the user cannot
   * recover from in place — e.g. a deleted, private, or otherwise inaccessible resource. Unlike
   * `isRecoverable`, this does not assert that a recovery path exists; it classifies the outcome as
   * an expected error rather than an app reliability failure. It receives the same downstream
   * treatment as a recoverable error — excluded from hard reliability, unblocks deferred UI, and for
   * Teams integrations unhides the iframe and prevents timeout — but is reported as a distinct
   * `FailedExpected` outcome so telemetry does not mislabel it as recoverable.
   *
   * Use this instead of `isRecoverable` whenever the rendered state is a valid terminal outcome with
   * no recovery action. Use `isRecoverable` only when the component genuinely offers a recovery path
   * (like a reload button).
   */
  readonly isExpectedTerminalState: boolean;
  readonly error?: Error;
}

export type ReportHeroComponentRenderedOptions =
  | ReportHeroComponentRenderedErrorOptions
  | ReportHeroComponentRenderedRecoverableErrorOptions
  | ReportHeroComponentRenderedExpectedTerminalErrorOptions;

export type ReportHeroComponentRendered = (
  component: HeroComponentName,
  options?: ReportHeroComponentRenderedOptions
) => void;

export interface RouteProps {
  readonly page: PageName;
  readonly path?: string;
  readonly hero: HeroComponentName[];
}

export interface PageTelemetryContextValue extends RouteProps {
  readonly pathname?: string;
  readonly isRoutelessApp: boolean;
  readonly reportHeroComponentRendered: ReportHeroComponentRendered;
  readonly hasHeroComponentRendered: boolean;
  readonly setRoute: (route: RouteProps, pathname: string) => void;
}

export const PageTelemetryContext: Context<PageTelemetryContextValue | undefined> = createContext<
  PageTelemetryContextValue | undefined
>(undefined);
